import React from "react";
import { motion } from "framer-motion";
import { Droplets, Home as HomeIcon, Layers, Square, Check } from "lucide-react";
import Layout from "../components/Layout";
import { useLang } from "../contexts/LanguageContext";
import { CONTACT, PRIMEWASH_LOGO_URL } from "../i18n/translations";

const HERO =
  "https://images.unsplash.com/photo-1774031159721-aec9230f38db?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwxfHxjbGVhbmluZyUyMGNvbmNyZXRlJTIwcHJlc3N1cmUlMjB3YXNoZXJ8ZW58MHx8fHwxNzc2Nzc5MTE0fDA&ixlib=rb-4.1.0&q=85";

const PrimeWashLogo = () => (
  <img
    src={PRIMEWASH_LOGO_URL}
    alt="AZOR PrimeWash"
    className="h-28 md:h-32 w-auto"
    data-testid="primewash-logo"
  />
);

const PrimeWash = () => {
  const { t } = useLang();

  const services = [
    { icon: Layers, title: t.primewash.s1Title, desc: t.primewash.s1Desc },
    { icon: HomeIcon, title: t.primewash.s2Title, desc: t.primewash.s2Desc },
    { icon: Square, title: t.primewash.s3Title, desc: t.primewash.s3Desc },
    { icon: Droplets, title: t.primewash.s4Title, desc: t.primewash.s4Desc },
  ];

  const whatsappHref = `https://wa.me/${CONTACT.whatsapp}?text=${encodeURIComponent(
    "Olá, gostaria de solicitar uma avaliação AZOR PrimeWash."
  )}`;

  return (
    <Layout darkMode>
      {/* Hero */}
      <section className="relative min-h-[88vh] overflow-hidden -mt-20 flex items-end" data-testid="primewash-hero">
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0E17] via-[#0A0E17]/70 to-[#0A0E17]" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pb-24 w-full pt-32">
          <PrimeWashLogo />
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mt-8 text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light tracking-tight text-white max-w-4xl leading-[1.05]"
            style={{ fontFamily: "Outfit, sans-serif" }}
            data-testid="primewash-title"
          >
            {t.primewash.headline}
          </motion.h1>
          <p className="mt-6 text-white/70 text-base md:text-lg max-w-3xl leading-relaxed">
            {t.primewash.subheadline}
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-5">
            <a
              href={whatsappHref}
              target="_blank"
              rel="noreferrer"
              data-testid="primewash-cta-btn"
              className="inline-flex items-center gap-3 bg-[#0066FF] text-white px-8 py-4 text-xs uppercase tracking-[0.2em] hover:bg-[#0052CC] transition"
            >
              {t.primewash.cta}
            </a>
            <a
              href={`tel:${CONTACT.phoneRaw}`}
              className="text-white/70 hover:text-white text-xs uppercase tracking-[0.2em] transition"
            >
              {CONTACT.phone}
            </a>
          </div>
        </div>
      </section>

      {/* Remoção + Método */}
      <section className="py-24 md:py-32" data-testid="primewash-remove">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-12 lg:gap-20">
          <div>
            <span className="uppercase tracking-[0.28em] text-xs text-[#0066FF]">{t.primewash.removeTitle}</span>
            <ul className="mt-8 space-y-5">
              {[t.primewash.r1, t.primewash.r2, t.primewash.r3].map((r, i) => (
                <motion.li
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  className="flex items-center gap-4 border-b border-white/10 pb-5"
                >
                  <Check className="w-5 h-5 text-[#0066FF] shrink-0" />
                  <span className="text-white text-xl md:text-2xl font-light" style={{ fontFamily: "Outfit, sans-serif" }}>
                    {r}
                  </span>
                </motion.li>
              ))}
            </ul>
          </div>
          <div className="flex items-center">
            <p className="text-white/80 text-lg md:text-xl leading-relaxed max-w-xl">
              {t.primewash.methodText}
            </p>
          </div>
        </div>
      </section>

      {/* Aplicações / Serviços */}
      <section className="py-24 md:py-32 border-t border-white/10" data-testid="primewash-services">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <span className="uppercase tracking-[0.28em] text-xs text-[#0066FF]">{t.primewash.servicesTitle}</span>
          <h2 className="mt-4 font-light tracking-tight text-3xl md:text-5xl text-white max-w-2xl" style={{ fontFamily: "Outfit, sans-serif" }}>
            {t.primewash.servicesTitle}
          </h2>

          <div className="mt-16 grid md:grid-cols-2 gap-px bg-white/10">
            {services.map(({ icon: Icon, title, desc }, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                className="bg-[#0A0E17] p-8 md:p-12 hover:bg-[#0E1422] transition-colors duration-500"
              >
                <Icon className="w-7 h-7 text-[#0066FF]" />
                <h3 className="mt-6 text-xl md:text-2xl text-white font-medium" style={{ fontFamily: "Outfit, sans-serif" }}>
                  {title}
                </h3>
                <p className="mt-3 text-white/60 leading-relaxed">{desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Abordagem */}
      <section className="py-24 md:py-32 border-t border-white/10" data-testid="primewash-approach">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <span className="uppercase tracking-[0.28em] text-xs text-[#0066FF]">{t.primewash.whyTitle}</span>
          <p
            className="mt-8 font-light tracking-tight text-2xl md:text-4xl text-white leading-[1.3]"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            {t.primewash.whyIntro}
          </p>
        </div>
      </section>

      {/* Posicionamento + CTA */}
      <section className="py-24 md:py-32 border-t border-white/10" data-testid="primewash-positioning">
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
          <span className="uppercase tracking-[0.28em] text-xs text-[#0066FF]">{t.primewash.positioningTitle}</span>
          <p
            className="mt-8 font-light tracking-tight text-2xl md:text-4xl text-white leading-[1.3] max-w-3xl mx-auto"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            {t.primewash.positioningText}
          </p>
          <a
            href={whatsappHref}
            target="_blank"
            rel="noreferrer"
            data-testid="primewash-final-cta"
            className="mt-12 inline-flex items-center gap-3 bg-[#0066FF] text-white px-10 py-5 text-xs uppercase tracking-[0.2em] hover:bg-[#0052CC] transition"
          >
            {t.primewash.cta}
          </a>
        </div>
      </section>
    </Layout>
  );
};

export default PrimeWash;
